import React, { useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import AppContext from '../../AppContext';
import languagify from '../../../utils/languagify';

const useStyles = makeStyles((theme) => ({
  viz: {
    height: 600,
    width: '100%',
    '& > div': {
      backgroundColor: 'transparent',
    },
    [theme.breakpoints.down('xs')]: {
      //   height: 200,
    },
  },
  legendWrapper: {
    width: '100%',
    minHeight: 25,
    height: 'auto',
    display: 'flex',
    justifyContent: 'center',
  },
  legends: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 10,
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 10px',
    fontSize: '0.9em',
    '@media (max-width: 550px)': {
      padding: '5px',
      fontSize: '0.8em',
    },
  },
  legendColor: {
    width: 10,
    height: 10,
    marginRight: 5,
  },
  legendText: {
    fontSize: '.9em',
  },
  slopeChart: {
    marginBottom: 50,
  },
}));

const lengendsContent = [
  {
    dimension: '[Gender - Comp]',
    legends: [
      {
        legend: 'Men',
        color: '#9EBFCE',
      },
      {
        legend: 'Women',
        color: '#BE7999',
      },
      {
        legend: 'Non-Binary',
        color: '#204635',
      },
    ],
  },
  {
    dimension: '[Race - Comp]',
    legends: [
      {
        legend: 'White',
        color: '#A7A4D4',
      },
      {
        legend: 'Asian',
        color: '#C17A82',
      },
      {
        legend: 'Indigenous',
        color: '#408CA0',
      },
      {
        legend: 'Mixed Race',
        color: '#4CDBD7',
      },
      {
        legend: 'Black',
        color: '#A55886',
      },
      {
        legend: 'Middle Eastern',
        color: '#E8CEAA',
      },
      {
        legend: 'Latin',
        color: '#A2C3B4',
      },
      {
        legend: 'Unknown',
        color: '#204635',
      },
    ],
  },
  {
    dimension: '[Job Role - Comp]',
    legends: [
      {
        legend: 'Full-Time',
        color: '#24796C',
      },
      {
        legend: 'Supervisors',
        color: '#CDB3A2',
      },
      {
        legend: 'Part-Time',
        color: '#2F8AC4',
      },
      {
        legend: 'Interns',
        color: '#B890D3',
      },
    ],
  },
];

const Slope = ({ dimension }) => {
  const elementRef = useRef();
  const vizRef = useRef();

  const { qlikApp, nebula } = useContext(AppContext);

  useEffect(async () => {
    if (!nebula) return;

    vizRef.current = await nebula.render({
      element: elementRef.current,
      type: 'slope',
      fields: [
        {
          qDef: {
            qFieldDefs: ['[Year - Comp]'],
          },
        },
        {
          qDef: {
            qFieldDefs: ['[Race - Comp]'],
          },
        },
        {
          qDef: {
            qDef: 'Sum([Number of Employees - Comp])',
          },
        },
      ],
    });
  }, [nebula]);

  useEffect(async () => {
    if (!qlikApp) return;

    // Checking if vizRef and vizRef.current exist before reading vizRef.current.id
    if (!vizRef || !vizRef.current || !vizRef.current.id) {
      return;
    }

    const appObj = await qlikApp.getObject(vizRef.current.id);

    // Apply change to Petroleum chart based on toggles
    await appObj.applyPatches([
      {
        qPath: '/qHyperCubeDef/qDimensions/1/qDef/qFieldDefs/0',
        qOp: 'replace',
        qValue: JSON.stringify(dimension),
      },
    ], true);
  }, [dimension]);

  const classes = useStyles();

  return (
    <Box className={classes.slopeChart}>
      <div id="slopeViz" className={classes.viz} ref={elementRef} />
      <Box className={classes.legends}>
        {
          lengendsContent.filter((m) => m.dimension === dimension)[0].legends.map((l) => (
            <Box className={classes.legend} key={l.legend}>
              <div className={classes.legendColor} style={{ backgroundColor: l.color }} />
              <Typography className={classes.legendText}>
                {languagify(l.legend)}
              </Typography>
            </Box>
          ))
        }
      </Box>
    </Box>
  );
};

Slope.propTypes = {
  dimension: PropTypes.string,
};

Slope.defaultProps = {
  dimension: '[Race - Comp]',
};

export default Slope;
